import { useCallback, useEffect, useState } from 'react';

/**
 * `useState` backed by `window.localStorage`.
 *
 * - SSR-safe (reads only on mount).
 * - Survives `JSON.parse` failures by falling back to `initialValue`.
 * - Synchronises across tabs via the native `storage` event.
 */
export function useLocalStorageState<T>(
  key: string,
  initialValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readStorage(key, initialValue));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore quota / private-mode errors — state still lives in memory.
    }
  }, [key, value]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    function onStorage(e: StorageEvent) {
      if (e.key !== key || e.newValue === null) {
        return;
      }
      try {
        setValue(JSON.parse(e.newValue) as T);
      } catch {
        // Ignore malformed payloads from other tabs.
      }
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [key]);

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue(next);
  }, []);

  return [value, set];
}

function readStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return fallback;
    }
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
